import { Fragment, memo } from 'react';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { DownloadIcon, ExternalLinkIcon } from 'foundations-assets';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import I18n from 'I18n';
import { getFileId, getFileMetadata
// @ts-expect-error resolvedAttachmentGetters is not typed
} from 'conversations-internal-schema/resolved-attachment/operators/resolvedAttachmentGetters';
import { getSignedUrl } from 'conversations-internal-schema/file-metadata/operators/getSignedUrl';
import { getType, getUrl
// @ts-expect-error fileMetadataGetters is not typed
} from 'conversations-internal-schema/file-metadata/operators/fileMetadataGetters';
import { AUDIO, IMG, MOVIE } from 'conversations-message-history/common-message-format/constants/fileAttachmentTypes';
import { usageTracker } from '../usage-tracker/usageTracker';
import { downloadEventConfig, downloadActionConfig } from '../usage-tracker/threadInteraction';
import { getUsageTrackingChannel } from '../usage-tracker/trackerChannelIdMapping';
import { FloatingButtonGroup } from 'foundations-components/transitional/components/FloatingButtonGroup';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InlineAudioAttachment = Loadable({
  loader: () => import('./InlineAudioAttachment').then(module => module.default),
  LoadingComponent: () => null
});
const InlineImageAttachment = Loadable({
  loader: () => import('./InlineImageAttachment').then(module => module.default),
  LoadingComponent: () => null
});
const InlineVideoAttachment = Loadable({
  loader: () => import('./InlineVideoAttachment').then(module => module.default),
  LoadingComponent: () => null
});
const MAX_ATTACHMENT_WIDTH = 300;
const AttachmentConfigs = {
  [IMG]: {
    component: InlineImageAttachment,
    hasHoverOptions: true,
    hasMaxWidth: true
  },
  [MOVIE]: {
    component: InlineVideoAttachment,
    hasHoverOptions: true,
    hasMaxWidth: true
  },
  [AUDIO]: {
    component: InlineAudioAttachment,
    hasHoverOptions: false,
    hasMaxWidth: false
  }
};
const InlineAttachmentWithOptions = ({
  fileAttachment,
  genericChannelId
}) => {
  const fileMetadata = getFileMetadata(fileAttachment);
  const fileId = getFileId(fileAttachment);
  const type = getType(fileMetadata);
  const url = getUrl(fileMetadata);
  const downloadUrl = getSignedUrl({
    fileId,
    isDownloadRequest: true
  });
  const AttachmentConfig = AttachmentConfigs[type];
  const AttachmentComponent = AttachmentConfig && AttachmentConfig['component'];
  const hasHoverOptions = AttachmentConfig && AttachmentConfig['hasHoverOptions'];
  const hasMaxWidth = AttachmentConfig && AttachmentConfig['hasMaxWidth'];
  const Attachment = /*#__PURE__*/_jsx(AttachmentComponent, {
    fileAttachment: fileAttachment,
    maxWidth: MAX_ATTACHMENT_WIDTH,
    genericChannelId: genericChannelId
  });
  const usageDownloadEvent = downloadEventConfig[type];
  const usageDownloadAction = downloadActionConfig[type];
  const handleDownload = () => {
    usageTracker.track(usageDownloadEvent, {
      action: usageDownloadAction,
      channel: getUsageTrackingChannel(genericChannelId)
    });
  };
  const handleClickAttachment = () => {
    usageTracker.track('click-attachment', {
      action: 'click attachment',
      channel: getUsageTrackingChannel(genericChannelId)
    });
  };
  return /*#__PURE__*/_jsx(View, {
    "data-test-id": "file-attachment-wrapper",
    as: "div",
    inlineSize: hasMaxWidth ? 'fit-content' : '100%',
    maxInlineSize: hasMaxWidth ? `${MAX_ATTACHMENT_WIDTH}px` : undefined,
    position: "relative",
    marginBlock: "250",
    marginInline: "0",
    children: hasHoverOptions ? /*#__PURE__*/_jsx(FloatingButtonGroup, {
      insetBlockStart: "0",
      insetInlineEnd: "0",
      buttonGroup: /*#__PURE__*/_jsxs(ButtonGroup, {
        insetBlockStart: "0",
        insetInlineEnd: "0",
        children: [/*#__PURE__*/_jsx(IconButton, {
          href: downloadUrl,
          size: "medium",
          download: true,
          onClick: handleDownload,
          children: /*#__PURE__*/_jsx(DownloadIcon, {
            "data-test-id": "download-attachment-button",
            size: "300",
            "aria-label": I18n.text('conversations-inbox-message-history.attachments.download')
          })
        }), /*#__PURE__*/_jsx(IconButton, {
          href: url,
          size: "medium",
          target: "_blank",
          onClick: handleClickAttachment,
          children: /*#__PURE__*/_jsx(ExternalLinkIcon, {
            "data-test-id": "open-attachment-button",
            size: "300",
            "aria-label": I18n.text('conversations-inbox-message-history.attachments.openInNewWindow')
          })
        })]
      }),
      children: Attachment
    }) : /*#__PURE__*/_jsx(Fragment, {
      children: Attachment
    })
  });
};
InlineAttachmentWithOptions.displayName = 'InlineAttachmentWithOptions';
export default /*#__PURE__*/memo(InlineAttachmentWithOptions);